/**
 * Author: Claude Code
 * Generation: Room creation modal with name and max participants input
 * Version: v3.2.119
 * Modified: 2025-10-15
 */
import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRoomStore } from '@store/RoomStore';

interface CreateRoomModalProps {
  show: boolean;
  onClose: () => void;
}

export default function CreateRoomModal({ show, onClose }: CreateRoomModalProps) {
  const navigate = useNavigate();
  const { createRoom, createRoomLoading, createRoomError } = useRoomStore();

  const [formData, setFormData] = useState({
    name: '',
    maxParticipants: 10,
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Room name is required');
      return;
    }

    const room = await createRoom(formData.name.trim(), formData.maxParticipants);
    if (room) {
      setFormData({ name: '', maxParticipants: 10 }); // Reset form
      onClose();
      navigate('/create-agent'); // Navigate to agent creation after successful room creation
    }
  };

  if (!show) return null;

  return (
    <div className={`modal-overlay ${show ? 'active' : ''}`}>
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">Create New Room</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {createRoomError && (
            <div className="error-message" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fee', color: '#c00', borderRadius: '8px' }}>
              {createRoomError}
            </div>
          )}

          <form id="createRoomForm" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Room Name *</label>
              <input
                type="text"
                className="form-input"
                placeholder="Epic Battle Arena"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                maxLength={50}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Max Participants</label>
              <select
                className="form-input"
                value={formData.maxParticipants}
                onChange={(e) => setFormData({ ...formData, maxParticipants: parseInt(e.target.value) })}
              >
                {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <option key={num} value={num}>{num} Players</option>
                ))}
              </select>
            </div>

            <div style={{ padding: '12px', backgroundColor: '#f0f8ff', borderRadius: '8px', fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              💡 Game starts when all players are ready
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-md"
              style={{ width: '100%' }}
              disabled={createRoomLoading || !formData.name.trim()}
            >
              {createRoomLoading ? 'Creating...' : 'Create Room'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
